import fetchApi from '../util/fetchApi';
import checkErrors from './checkErrors';
import checkDocumentResponse, { STATUS } from './checkDocumentResponse';
import { UWEEntity, WFAccessToken } from './types';

type UnknownObject = {
  [key: string]: unknown;
};

const getAccessTokenHeaders = (wfAccessToken: WFAccessToken) => {
  if (!wfAccessToken) return {};

  return {
    headers: {
      'Workflow-Token': wfAccessToken?.hash,
    },
  };
};

// STILL TODO: TS => add return types for all functions
const UWEEntityApi = {
  async getWorkTray({
    resource,
    offset,
    size,
    sortBy,
    abortController,
    ...filters
  }: {
    resource: string;
    offset: number;
    size: number;
    sortBy: string;
    abortController?: AbortController;
  } & UnknownObject): Promise<{
    count: number;
    from: number;
    items: UWEEntity[];
    to: number;
  }> {
    const result = await checkErrors(await fetchApi.api.get(resource, { query: { offset, size, sortBy, ...filters }, abortController }));

    return result.data ? result.data : result;
  },

  async getOpenCases() {
    return checkErrors(await fetchApi.api.get('uwe-entities/work-cases/'));
  },

  async getUWEEntity({ entityId, wfAccessToken }: { entityId: number; wfAccessToken: WFAccessToken }) {
    const entity = await checkErrors(await fetchApi.api.get(`uwe-entities/${entityId}`, getAccessTokenHeaders(wfAccessToken)));

    return entity;
  },

  async getUWEEntityStatus({ entityId, wfAccessToken }: { entityId: number; wfAccessToken: WFAccessToken }) {
    return (await checkErrors(await fetchApi.api.get(`uwe-entities/${entityId}/status`, getAccessTokenHeaders(wfAccessToken)))).data;
  },

  // Resolves formpage + every SubformField it references (recursively) under this entity's
  // effective FormVersioningMode — a `{ [name]: { formId, version } }` map. Replaces blindly
  // fetching a form by name, which ignores Draft/Scheduled/Active/Pinned resolution entirely.
  async resolveFormVersions({
    entityId,
    formpage,
    wfAccessToken,
  }: {
    entityId: number;
    formpage: string;
    wfAccessToken: WFAccessToken;
  }): Promise<Record<string, { formId: number; version: number }>> {
    return (await checkErrors(await fetchApi.api.get(`uwe-entities/${entityId}/resolve/${formpage}`, getAccessTokenHeaders(wfAccessToken)))).data;
  },

  async performActionOnUWEEntity({ id, action }: { id: number; action: string }) {
    return checkErrors(await fetchApi.api.post(`uwe-entities/${id}/perform-action/${action}`));
  },

  async getUWEEntityHistory({ id, wfAccessToken }: { id: number; wfAccessToken: WFAccessToken }) {
    return (await checkErrors(await fetchApi.api.get(`uwe-entities/${id}/history`, getAccessTokenHeaders(wfAccessToken)))).data;
  },

  async getWorkflowInfoForSlugForAnonymous({ slug, tenant }: { slug: string; tenant: number } = { slug: '', tenant: 0 }) {
    return (await checkErrors(await fetchApi.api.get(`anon/${tenant}/workflows/${slug}/info`))).data;
  },

  async getWorkflowInfoForSlug({ slug }: { slug: string } = { slug: '' }) {
    return (await checkErrors(await fetchApi.api.get(`workflows/info/${slug}`))).data;
  },
  async getWorkflowInfoForId({ id }: { id: number } = { id: 0 }) {
    return (await checkErrors(await fetchApi.api.get(`workflows/info/workflow:${id}`))).data;
  },

  async getWorkflowStates({ workflowId, workflowName }: { workflowId?: number; workflowName?: string } = { workflowId: 0 }) {
    const query = { id: `workflow:${workflowName || workflowId}` };

    return (await checkErrors(await fetchApi.lookup.get('workflow-states', { query }))).data;
  },

  async getWorkflowStatesForEntity({ entityId }: { entityId: number } = { entityId: 0 }) {
    const query = { id: `entity:${entityId}` };

    return (await checkErrors(await fetchApi.lookup.get('workflow-states', { query }))).data;
  },

  async getWorkflowStatusMap({ id }: { id: number } = { id: 0 }) {
    const states = (await UWEEntityApi.getWorkflowStatesForEntity({ entityId: id })) || [];

    return states.reduce((_, { name, title }) => {
      _[name] = title || name;

      return _;
    }, {});
  },

  async getUWEEntityLog({ id, wfAccessToken }: { id: number; wfAccessToken: WFAccessToken }) {
    return checkErrors(await fetchApi.api.get(`uwe-entities/${id}/log`, getAccessTokenHeaders(wfAccessToken)));
  },

  async getUWEEntityProgressSteps({ entityId, fromParent, wfAccessToken }: { entityId: number; fromParent: boolean; wfAccessToken: WFAccessToken }) {
    const query = {
      fromParent: fromParent ? '1' : undefined,
    };

    return checkErrors(await fetchApi.api.get(`uwe-entities/${entityId}/progress-steps`, { ...getAccessTokenHeaders(wfAccessToken), query }));
  },

  async teleportUWEEntityToLogEntry({ id, stepIdx }: { id: number; stepIdx: number }) {
    return checkErrors(await fetchApi.api.post(`uwe-entities/${id}/log/teleport`, { data: { index: stepIdx } }));
  },

  async testDocumentExpression({ id, options }: { id: number; options: Object }) {
    return checkErrors(await fetchApi.api.put(`uwe-entities/${id}/test/document-expression`, { data: options }));
  },

  async testPreviewDocument({ id, options }: { id: number; options: Object }) {
    return checkErrors(await fetchApi.api.put(`uwe-entities/${id}/test/document-test`, { data: options }));
  },

  async testInternalNotificationMessage({ id, object }: { id: number; object: Object }) {
    return checkErrors(await fetchApi.api.put(`uwe-entities/${id}/test/internal-notification`, { data: object }));
  },

  async previewMetadata({ id, metadataObjectMap }: { id: number; metadataObjectMap: Object }) {
    return (await checkErrors(await fetchApi.api.post(`uwe-entities/${id}/preview-metadata`, { data: metadataObjectMap }))).data;
  },

  async getUWEEntityAssignments({ id, wfAccessToken }: { id: number; wfAccessToken: WFAccessToken }) {
    return checkErrors(await fetchApi.api.get(`uwe-entities/${id}/assignments`, getAccessTokenHeaders(wfAccessToken)));
  },

  async setUWEEntityAssignment({ id, roleId, userId }: { id: number; roleId: number; userId: number }) {
    return checkErrors(
      await fetchApi.api.post(`uwe-entities/${id}/assign`, {
        data: { roleId, userId },
      })
    );
  },

  async setEntityWorkflowStep({ id, step }: { id: number; step: Object }) {
    return checkErrors(
      await fetchApi.api.post(`uwe-entities/${id}/set-step`, {
        data: { step },
      })
    );
  },

  async createUWEEntity({ page, formData, wfAccessToken }: { page: string; formData: Object; wfAccessToken: WFAccessToken }) {
    return checkErrors(
      await fetchApi.api.post('uwe-entities/', {
        ...getAccessTokenHeaders(wfAccessToken),
        data: { ...formData, page, workflowAccessTokenId: wfAccessToken.id },
      })
    );
  },

  async updateUWEEntity({ id, page, formData, wfAccessToken }: { id: number; page: string; formData: Object; wfAccessToken: WFAccessToken }) {
    return checkErrors(await fetchApi.api.put(`uwe-entities/${id}/${page}`, { ...getAccessTokenHeaders(wfAccessToken), data: formData }));
  },

  async updateUWEEntityData({ id, data, wfAccessToken }: { id: number; data: Object; wfAccessToken: WFAccessToken }) {
    return checkErrors(
      await fetchApi.api.put(`uwe-entities/${id}/data`, {
        data: { data },
        ...getAccessTokenHeaders(wfAccessToken),
      })
    );
  },

  async createUWEEntityAsAnonymous({
    payload: { page, formData },
    slug,
    tenant,
  }: {
    payload: { id: number; page: string; formData: Object };
    slug: string;
    tenant: number;
  }) {
    return checkErrors(await fetchApi.api.post(`anon/${tenant}/workflows/${slug}/uwe-entities/new`, { data: { ...formData, page } }));
  },

  async updateUWEEntityAsAnonymous({
    payload: { id, page, formData },
    slug,
    tenant,
  }: {
    payload: { id: number; page: string; formData: Object };
    slug: string;
    tenant: number;
  }) {
    return checkErrors(await fetchApi.api.put(`anon/${tenant}/workflow/${slug}/uwe-entities/${id}/${page}`, { data: formData }));
  },

  async addProducts({ id, productIds }: { id: number; productIds: Object }) {
    return checkErrors(await fetchApi.api.post(`uwe-entities/${id}/products`, { data: { productIds } }));
  },

  async awaitDocuments({ id, page, wfAccessToken }: { id: number; page: string; wfAccessToken: WFAccessToken }) {
    return checkErrors(
      await fetchApi.api.put(`uwe-entities/${id}/await-documents`, {
        ...getAccessTokenHeaders(wfAccessToken),
        data: {
          page,
        },
      })
    );
  },

  async createUWEEntityNote({ entityId, note, wfAccessToken }: { entityId: number; note: string; wfAccessToken: WFAccessToken }) {
    return checkErrors(
      await fetchApi.api.post(`uwe-entities/${entityId}/notes`, { ...getAccessTokenHeaders(wfAccessToken), data: note, json: true })
    );
  },

  async getUWEEntityNotes({ entityId, wfAccessToken }: { entityId: number; wfAccessToken: WFAccessToken }) {
    return checkErrors(await fetchApi.api.get(`uwe-entities/${entityId}/notes`, getAccessTokenHeaders(wfAccessToken)));
  },

  async getRoleAssignments({ id, wfAccessToken }: { id: number; wfAccessToken: WFAccessToken }) {
    return checkErrors(await fetchApi.api.get(`uwe-entities/${id}/role-assignments`, getAccessTokenHeaders(wfAccessToken)));
  },

  async getUWEEntityZipDownloadUrl({ id, filter, wfAccessToken }: { id: number; filter: Object; wfAccessToken: WFAccessToken }) {
    return (
      await checkErrors(
        await fetchApi.api.get(`uwe-entities/${id}/download-documents`, { ...getAccessTokenHeaders(wfAccessToken), query: { filter } })
      )
    ).data;
  },
  async getWorkflowRoles({ id, wfAccessToken }: { id: number; wfAccessToken: WFAccessToken }) {
    return checkErrors(await fetchApi.api.get(`uwe-entities/${id}/workflow-roles`, getAccessTokenHeaders(wfAccessToken)));
  },
  async getProgressByOrganization({ id }: { id: number }) {
    return checkErrors(await fetchApi.api.get(`uwe-entities/getProgressByOrganization/${id}`));
  },

  async generatePreviewFor({ id, data }: { id: number; data: Object }) {
    return (
      await checkErrors(
        await fetchApi.api.post(`uwe-entities/${id || 0}/form-preview`, {
          data,
          json: true,
        })
      )
    ).data;
  },

  async verifyPreviewGeneration({ referenceId }: { referenceId: number }) {
    const response = checkDocumentResponse((await checkErrors(await fetchApi.api.get(`uwe-entities/form-preview/verify/${referenceId}`))).data);

    if (response.status === STATUS.NOTFOUND || response.status === STATUS.ERROR) {
      throw new Error('Error buscando documento.');
    }

    return response;
  },

  async submissionFromEmail({ token }: { token: string }) {
    return (await checkErrors(await fetchApi.api.get(`uwe-entities/submissionFromEmail/${token}`))).data;
  },

  async createWfAccessTokenHash({
    workflowId,
    uweEntityId,
    canView,
    canCreate,
    canUpdate,
  }: {
    workflowId?: number | null;
    uweEntityId?: number;
    canView: boolean;
    canCreate: boolean;
    canUpdate: boolean;
  }) {
    return checkErrors(
      await fetchApi.api.post('uwe-entities/workflow-access-token', {
        data: { workflowId, uweEntityId, canView, canCreate, canUpdate },
      })
    );
  },
};

export default UWEEntityApi;
